import { FlowType } from "@redotech/redo-model/return-flow";
import { Team } from "@redotech/redo-model/team";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Edge, Node } from "@xyflow/react";
import { MutableRefObject } from "react";
import { NodeData } from "../components/flow-step-node";
import { getLayoutedElements } from "./layout";

function calculateStartNodePosition(
  savedPositions: Map<string, { x: number; y: number }>,
): { x: number; y: number } {
  if (savedPositions.size === 0) {
    return { x: 0, y: 0 };
  }

  const positions = Array.from(savedPositions.values());
  const minY = Math.min(...positions.map((p) => p.y));

  const topRowNodes = positions.filter((p) => Math.abs(p.y - minY) < 50);

  const avgX =
    topRowNodes.length > 0
      ? topRowNodes.reduce((sum, p) => sum + p.x, 0) / topRowNodes.length
      : positions.reduce((sum, p) => sum + p.x, 0) / positions.length;

  return { x: avgX, y: minY - 150 };
}

export function createNodesFromSteps(
  steps: Map<StepId, Step>,
  flowType: FlowType,
  userNodePositions: Map<string, { x: number; y: number }>,
  selectedNodeIdsRef: MutableRefObject<Set<string>>,
  team?: Team,
): {
  nodes: Node<NodeData>[];
  nodePositions: Map<string, { x: number; y: number }>;
} {
  const nodes: Node<NodeData>[] = [];
  const basicEdges: Edge[] = [];
  const nodePositions = new Map<string, { x: number; y: number }>();

  const validStepIds = new Set(steps.keys());

  for (const [stepId, step] of steps) {
    nodes.push({
      id: stepId,
      type: "flowStep",
      position: { x: 0, y: 0 },
      data: { step, stepId, flowType, allSteps: steps, nodePositions, team },
      deletable: stepId !== "start",
      draggable: true,
      selected: false,
    });

    const downstreams = step.downstream();
    for (const downstream of downstreams) {
      if (downstream.id && validStepIds.has(downstream.id)) {
        basicEdges.push({
          id: `${stepId}-${downstream.id}`,
          source: stepId,
          target: downstream.id,
          type: "smoothstep",
        });
      }
    }
  }

  const nonStartStepIds = Array.from(steps.keys()).filter(
    (id) => id !== "start",
  );
  const allNonStartNodesHaveSavedPositions = nonStartStepIds.every((stepId) =>
    userNodePositions.has(stepId),
  );

  let positionedNodes: Node<NodeData>[];

  if (allNonStartNodesHaveSavedPositions && nonStartStepIds.length > 0) {
    const startNodePosition =
      userNodePositions.get("start") ||
      calculateStartNodePosition(userNodePositions);

    positionedNodes = nodes.map((node) => {
      const userPosition = userNodePositions.get(node.id);
      const finalPosition =
        node.id === "start" ? startNodePosition : userPosition!;

      nodePositions.set(node.id, finalPosition);

      const shouldBeSelected = selectedNodeIdsRef.current.has(node.id);

      return { ...node, position: finalPosition, selected: shouldBeSelected };
    });
  } else {
    const layoutedElements = getLayoutedElements(nodes, basicEdges);
    const autoLayoutNodes = layoutedElements.nodes;

    positionedNodes = autoLayoutNodes.map((node) => {
      const userPosition = userNodePositions.get(node.id);
      const finalPosition = userPosition || node.position;

      nodePositions.set(node.id, finalPosition);

      const shouldBeSelected = selectedNodeIdsRef.current.has(node.id);

      return { ...node, position: finalPosition, selected: shouldBeSelected };
    });
  }

  return { nodes: positionedNodes, nodePositions };
}
